import Translatable from "@/components/translatable_text/Translatable";
import { ApplicationItem } from "@/types/admin_types";
import React from "react";


interface Props {
  application: ApplicationItem;
}


const Info = ({
  label,
  value,
}: {
  label: string;
  value: string | number | React.ReactNode;
}) => (
  <div>
    <p className="text-sm text-gray-500 mb-1"><Translatable>{label}</Translatable></p>
    <p className="text-gray-800 font-medium"><Translatable>{value || "—"}</Translatable></p>
  </div>
);


const JobApplicationDetails: React.FC<Props> = ({ application }) => {
  return (
    <div className="max-w-5xl mx-auto bg-gray-100 shadow-sm rounded-2xl p-8 border border-gray-100">

      <div className="flex  items-center justify-between border-b pb-4">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-3">
          <Translatable>
            Job Application Details
          </Translatable>
        </h2>
        <span className="text-sm text-gray-500">
          <Translatable>
            Application ID 
          </Translatable>
          :{application.id}
        </span>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8 pt-20">
        <Info label="Full Name" value={application.full_name} />
        <Info label="Email" value={application.email} />
        <Info label="Phone" value={application.phone} />
        <Info label="Nationality" value={application.nationality} />
      </div>

      {/* Academic Info */}
      <h2 className="text-2xl font-semibold text-gray-700 mb-3">
        <Translatable>
          Academic Information
        </Translatable>
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
        <Info label="University" value={application.university} />
        <Info label="Department" value={application.department} />
        <Info label="Major" value={application.major} />
        <Info
          label="GPA"
          value={`${application.gpa} / ${application.gpa_system}`}
        />
        <Info
          label="Qualification"
          value={application.academic_qualification}
        />
      </div>

      {/* Application Info */}
      <h2 className="text-2xl font-semibold text-gray-700 mb-3">
        <Translatable>
          Application Information
        </Translatable>
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
        <Info label="Application Type" value={application.application_type} />
        <div>
          <p className="text-sm text-gray-500 mb-1"><Translatable>Status</Translatable></p>
          <p className="text-gray-800 font-medium">
            <span
              className={`px-3 py-1 rounded-full text-sm ${
                application.status === "pending"
                  ? "bg-yellow-100 text-yellow-700"
                  : application.status === "approved"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {application.status}
            </span>
          </p>
        </div>
     
        <Info
          label="Submitted On"
          value={new Date(application.created_at).toLocaleDateString()}
        />
      </div>

      {/* Cover Letter */}
      <h2 className="text-2xl font-semibold text-gray-700 mb-3">
        <Translatable>
          Cover Letter
        </Translatable>
      </h2>
      <p className="text-gray-700 whitespace-pre-line leading-relaxed mb-6">
        <Translatable>
          {application.cover_letter}
        </Translatable>
      </p>

      {/* Resume */}
      <div className="flex items-center justify-between border-t pt-4">
        <a
          href={application.resume}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 font-medium hover:underline flex items-center gap-1"
        >
          📄 {' '}
          <Translatable>
            View Resume
          </Translatable> {' '}
          (PDF)
        </a>
        <a
          href={application.resume}
          download
          className="text-blue-600 font-medium hover:underline flex items-center gap-1"
        >
          ⬇️  {" "}
          <Translatable>
            Download Resume
          </Translatable> {" "}
          (PDF)
        </a>
      </div>
    </div>
  );
};

export default JobApplicationDetails;